import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonWrapperProps"];
import { cloneElement, useState, useEffect, useRef, useCallback, Children } from 'react';
import { ButtonWrapper } from '../ButtonWrapper/ButtonWrapper';
import { Dropdown } from '../Dropdown';
import { List, ListItem } from '../List';
import { HiddenMeasure } from '../../utils/HiddenMeasure';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getElementWidth = el => {
  const rect = el.getBoundingClientRect();
  const computedStyles = getComputedStyle(el);
  return rect.width + parseFloat(computedStyles.marginLeft) + parseFloat(computedStyles.marginRight);
};
const getAvailableWidth = (parentEl, outerEl) => {
  const computedStyles = getComputedStyle(parentEl, null);
  if (!computedStyles) {
    return Infinity;
  }
  const innerWidth = getElementWidth(parentEl) - parseFloat(computedStyles.paddingLeft) - parseFloat(computedStyles.paddingRight);
  let availableWidth = innerWidth;
  if (computedStyles.display === 'flex' && computedStyles.flexDirection === 'row' && computedStyles.flexWrap === 'nowrap') {
    const childNodes = parentEl.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      if (childNodes[i] !== outerEl) {
        availableWidth -= getElementWidth(childNodes[i]);
      }
    }
  }
  return availableWidth;
};
const renderExcessActionsDropdown = (moreActionsRenderer, excessActions) => {
  return /*#__PURE__*/cloneElement(moreActionsRenderer(excessActions), {
    'data-more-tabs': true
  });
};
export const ActionsCollapser = props => {
  const [excessActionsCount, setExcessActionsCount] = useState(0);
  const childArray = Children.toArray(props.children);
  const _outerEl = useRef(null);
  const _measureEl = useRef(null);
  const _resizeObserver = useRef(null);
  const stopObserver = useCallback(() => {
    var _resizeObserver$curre;
    (_resizeObserver$curre = _resizeObserver.current) === null || _resizeObserver$curre === void 0 || _resizeObserver$curre.disconnect();
  }, [_resizeObserver]);
  useEffect(() => {
    var _outerEl$current;
    const parentElement = (_outerEl$current = _outerEl.current) === null || _outerEl$current === void 0 || (_outerEl$current = _outerEl$current.parentElement) === null || _outerEl$current === void 0 ? void 0 : _outerEl$current.parentElement;
    if (parentElement && _measureEl.current) {
      _resizeObserver.current = new ResizeObserver(getExcessActionsCount);
      _resizeObserver.current.observe(parentElement);
      _resizeObserver.current.observe(_measureEl.current);
    }
    return stopObserver;
  });
  const moreActionsRenderer = excessActions => {
    const {
      dropdownProps
    } = props;
    return /*#__PURE__*/_jsx(Dropdown, Object.assign({
      buttonText: unescapedText('salesUI.UITabs.moreDropdownLabel')
    }, dropdownProps, {
      children: /*#__PURE__*/_jsx(List, {
        children: excessActions.map((action, index) => /*#__PURE__*/_jsx(ListItem, {
          children: action
        }, index))
      })
    }));
  };
  function getExcessActionsCount() {
    const measureEl = _measureEl.current;
    const outerEl = _outerEl && _outerEl.current ? _outerEl.current.parentElement : null;
    const parentEl = outerEl ? outerEl.parentElement : null;
    if (measureEl == null || outerEl == null || parentEl == null) {
      return;
    }
    const horizontalGap = _outerEl.current ? Math.abs(parseInt(getComputedStyle(_outerEl.current).marginLeft, 10)) : 0;
    const containerWidth = Math.ceil(getAvailableWidth(parentEl, outerEl));
    let totalActionsWidth = 0;
    const totalActionsArray = Array.from(measureEl.children).filter(action => !action.getAttribute('data-more-tabs'));

    // Don't add in the horizontal gap for zero-width elements
    const getElementWidthPlusHorizontalGap = element => {
      let elementWidth = getElementWidth(element);
      if (elementWidth > 0) {
        elementWidth += horizontalGap;
      }
      return elementWidth;
    };
    totalActionsArray.forEach(element => {
      totalActionsWidth += getElementWidthPlusHorizontalGap(element);
    });
    if (totalActionsWidth <= containerWidth) {
      if (excessActionsCount !== 0) setExcessActionsCount(0);
      return;
    }
    const moreTabsElement = document.querySelector('[data-more-tabs]');
    const moreDropDown = moreTabsElement ? getElementWidth(moreTabsElement) + horizontalGap : 0;
    let calcActionsWidth = moreDropDown;
    let newExcessActionsCount = totalActionsArray.length;
    for (let i = 0; i < totalActionsArray.length; i++) {
      const actionWidth = getElementWidthPlusHorizontalGap(totalActionsArray[i]);
      if (calcActionsWidth + actionWidth > containerWidth) break;
      calcActionsWidth += actionWidth;
      newExcessActionsCount -= 1;
    }
    if (newExcessActionsCount !== excessActionsCount) {
      setExcessActionsCount(newExcessActionsCount);
    }
  }
  const getExcessActions = () => {
    const excessActions = childArray.slice(childArray.length - excessActionsCount);
    return excessActions;
  };
  const getVisibleActions = () => {
    const visibleActions = childArray.slice(0, childArray.length - excessActionsCount);
    return visibleActions;
  };
  const visibleActions = getVisibleActions();
  const excessActions = getExcessActions();
  const excessActionsDropdown = renderExcessActionsDropdown(moreActionsRenderer, excessActions);
  const {
      buttonWrapperProps
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const buttonWrapperPropsWithRef = Object.assign({}, buttonWrapperProps, {
    flexRef: _outerEl
  }, rest);

  // Wrap children meant to be rendered in HiddenMeasure with spans
  // so that even null-rendering children will be measurable as
  // elements with 0-width and so that the number of dom elements
  // rendered in the hidden measure matches the number of react element
  // children passed to the ActionsCollapser – this matching is crucial
  // for the visible/excess actions calculations to maintain accuracy.
  const spanWrappedChildren = Children.map(props.children, (child, index) => {
    return /*#__PURE__*/_jsx("span", {
      children: child
    }, index);
  });
  return /*#__PURE__*/_jsxs(ButtonWrapper, Object.assign({}, buttonWrapperPropsWithRef, {
    children: [visibleActions, excessActionsCount > 0 && excessActionsDropdown, /*#__PURE__*/_jsxs(HiddenMeasure, {
      ref: _measureEl,
      blockSize: "0",
      children: [spanWrappedChildren, excessActionsDropdown]
    })]
  }));
};
ActionsCollapser.displayName = 'ActionsCollapser';